import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Building, Calendar, MapPin, Clock, Shield, AlertCircle, Loader2, ArrowLeft, ArrowRight, Home,
} from 'lucide-react';
import PublicLayout from '../components/public/PublicLayout';
import DarkSection from '../components/public/DarkSection';
import { Reveal } from '../components/public/motion';
import { publicSupabase as supabase } from '../lib/publicSupabaseClient';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { formatDate, formatDateTime } from '../utils/dateFormatter';
import { cleanHTML, fixSelectionCriteriaNumbering, RICH_TEXT_PROSE_CLASSES as PROSE } from '../utils/richTextRenderer';

const CLEARANCE_COLORS = {
  TSPV: 'bg-purple-100 text-purple-700 border-purple-200',
  NV2: 'bg-blue-100 text-blue-700 border-blue-200',
  NV1: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  Baseline: 'bg-slate-100 text-slate-700 border-slate-200',
  None: 'bg-slate-50 text-slate-500 border-slate-100',
};

const isEOIDate = (dateStr) => {
  if (!dateStr) return false;
  return new Date(dateStr).getFullYear() > 2050;
};

const DetailItem = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3">
    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100">
      <Icon className="h-4 w-4 text-gray-500" />
    </div>
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{label}</p>
      <div className="mt-0.5 text-sm font-medium text-gray-900">{children}</div>
    </div>
  </div>
);

const PublicJobListing = () => {
  const { id } = useParams();
  const { userRole, loading: authLoading } = useSupabaseAuth();
  const [rfq, setRfq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!authLoading) fetchRFQ();
  }, [id, userRole, authLoading]);

  const fetchRFQ = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('rfqs')
        .select('*, client:clients(name)')
        .eq('id', id)
        .single();

      if (error) throw error;

      if (!data) {
        setError('Job listing not found.');
        setRfq(null);
      } else {
        const isAdminOrRecruiter = userRole === 'admin' || userRole === 'recruiter';
        if (isAdminOrRecruiter || data.display_status !== 'hidden') {
          setRfq(data);
          setError(null);
        } else {
          setError('This job listing is not available.');
          setRfq(null);
        }
      }
    } catch (err) {
      console.error('Error fetching RFQ:', err);
      setError(`Job listing not found or is no longer available.`);
      setRfq(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex flex-col items-center justify-center py-40 gap-4 text-gray-500">
          <Loader2 className="h-8 w-8 animate-spin text-red-500" />
          <p className="text-sm">Loading job listing…</p>
        </div>
      </PublicLayout>
    );
  }

  if (error || !rfq) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-3xl px-4 py-24 text-center">
          <div className="rounded-3xl border border-red-100 bg-red-50 p-14">
            <AlertCircle className="mx-auto h-10 w-10 text-red-400 mb-4" />
            <h2 className="font-display text-2xl font-bold text-gray-900">Role not found</h2>
            <p className="mt-3 text-gray-600">{error}</p>
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Link
                to="/jobs"
                className="inline-flex items-center gap-2 rounded-full bg-red-500 px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-red-600"
              >
                <ArrowLeft className="h-4 w-4" /> Back to Job Board
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-full border-2 border-gray-200 px-6 py-3 text-sm font-semibold text-gray-700 transition-all hover:border-gray-400"
              >
                <Home className="h-4 w-4" /> Go to Home
              </Link>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  const closingDisplay = rfq.client_close_datetime
    ? isEOIDate(rfq.client_close_datetime)
      ? 'Rolling: no fixed closing date'
      : formatDateTime(rfq.client_close_datetime, { style: 'long' })
    : null;

  return (
    <PublicLayout>
      {/* Dark hero header */}
      <DarkSection glow="top-right" className="pb-14 pt-32 md:pb-20 md:pt-44">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
            backgroundSize: '56px 56px',
          }}
        />
        <div className="relative mx-auto max-w-4xl px-4 md:px-8">
          <Link
            to="/jobs"
            className="mb-8 inline-flex items-center gap-2 text-sm text-gray-400 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Job Board
          </Link>

          {rfq.work_arrangement && (
            <p className="mt-2 text-xs font-semibold uppercase tracking-widest text-red-400">
              {rfq.work_arrangement}
            </p>
          )}
          <h1 className="mt-2 font-display text-4xl font-extrabold leading-tight tracking-tight md:text-5xl">
            {rfq.title}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-400">
            {rfq.client?.name && (
              <span className="inline-flex items-center gap-1.5">
                <Building className="h-4 w-4" /> {rfq.client.name}
              </span>
            )}
            {rfq.ref_code && <span className="text-gray-500">{rfq.ref_code}</span>}
            {closingDisplay && (
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-4 w-4" /> {closingDisplay}
              </span>
            )}
          </div>

          <div className="mt-8">
            <Link
              to={`/job/${id}/apply`}
              className="group inline-flex items-center gap-2 rounded-full bg-red-500 px-8 py-4 font-semibold text-white transition-all duration-300 hover:bg-red-600 hover:shadow-xl hover:shadow-red-500/30"
            >
              Apply Now
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </DarkSection>

      {/* Content */}
      <div className="bg-gray-50 py-12 md:py-16">
        <div className="mx-auto max-w-4xl space-y-6 px-4 md:px-8">

          {/* Position details */}
          <Reveal>
            <section className="rounded-3xl border border-gray-200 bg-white p-8">
              <h2 className="font-display text-xs font-bold uppercase tracking-wider text-gray-400">
                Position Details
              </h2>
              <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3">
                {rfq.work_arrangement && (
                  <DetailItem icon={Home} label="Work Arrangement">
                    {rfq.work_arrangement}
                  </DetailItem>
                )}
                {rfq.locations?.length > 0 && (
                  <DetailItem icon={MapPin} label="Location">
                    {Array.isArray(rfq.locations) ? rfq.locations.join(', ') : rfq.locations}
                  </DetailItem>
                )}
                {rfq.clearance_required && (
                  <DetailItem icon={Shield} label="Security Clearance">
                    <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-0.5 text-xs font-semibold ${CLEARANCE_COLORS[rfq.clearance_required] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                      {rfq.clearance_required}
                    </span>
                  </DetailItem>
                )}
                {rfq.estimated_start_date && (
                  <DetailItem icon={Calendar} label="Estimated Start">
                    {formatDate(rfq.estimated_start_date, { style: 'long' })}
                  </DetailItem>
                )}
                {rfq.initial_term_months && (
                  <DetailItem icon={Clock} label="Contract Duration">
                    {rfq.initial_term_months} months
                    {rfq.extension_options && (
                      <span className="block text-xs font-normal text-gray-500 mt-0.5">
                        + {rfq.extension_options}
                      </span>
                    )}
                  </DetailItem>
                )}
              </div>
            </section>
          </Reveal>

          {/* About the role */}
          {rfq.description && (
            <Reveal delay={0.05}>
              <section className="rounded-3xl border border-gray-200 bg-white p-8">
                <h2 className="font-display text-xs font-bold uppercase tracking-wider text-gray-400">
                  About the Role
                </h2>
                <div
                  className={`mt-6 ${PROSE}`}
                  dangerouslySetInnerHTML={{ __html: cleanHTML(rfq.description) }}
                />
              </section>
            </Reveal>
          )}

          {/* Selection criteria */}
          {rfq.selection_criteria && (
            <Reveal delay={0.1}>
              <section className="rounded-3xl border border-gray-200 bg-white p-8">
                <h2 className="font-display text-xs font-bold uppercase tracking-wider text-gray-400">
                  Selection Criteria
                </h2>
                <div
                  className={`mt-6 ${PROSE}`}
                  dangerouslySetInnerHTML={{ __html: fixSelectionCriteriaNumbering(cleanHTML(rfq.selection_criteria)) }}
                />
              </section>
            </Reveal>
          )}

          {/* About the organisation */}
          {(rfq.about_company || rfq.about_team || rfq.about_project) && (
            <Reveal delay={0.15}>
              <section className="rounded-3xl border border-gray-200 bg-white p-8">
                <h2 className="font-display text-xs font-bold uppercase tracking-wider text-gray-400">
                  About {rfq.client?.name || 'the Organisation'}
                </h2>
                <div className="mt-6 space-y-8">
                  {rfq.about_company && (
                    <div>
                      <h3 className="mb-3 text-sm font-semibold text-gray-700">The Organisation</h3>
                      <div className={PROSE} dangerouslySetInnerHTML={{ __html: cleanHTML(rfq.about_company) }} />
                    </div>
                  )}
                  {rfq.about_team && (
                    <div>
                      <h3 className="mb-3 text-sm font-semibold text-gray-700">The Team</h3>
                      <div className={PROSE} dangerouslySetInnerHTML={{ __html: cleanHTML(rfq.about_team) }} />
                    </div>
                  )}
                  {rfq.about_project && (
                    <div>
                      <h3 className="mb-3 text-sm font-semibold text-gray-700">The Project</h3>
                      <div className={PROSE} dangerouslySetInnerHTML={{ __html: cleanHTML(rfq.about_project) }} />
                    </div>
                  )}
                </div>
              </section>
            </Reveal>
          )}

          {/* Apply CTA */}
          <Reveal delay={0.2}>
            <DarkSection glow="top-center" className="rounded-3xl p-8 md:p-10">
              <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="font-display text-2xl font-extrabold">Ready to apply?</h3>
                  <p className="mt-2 text-gray-400">Submit your application and we'll be in touch shortly.</p>
                </div>
                <Link
                  to={`/job/${id}/apply`}
                  className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-red-500 px-8 py-4 font-semibold text-white transition-all duration-300 hover:bg-red-600 hover:shadow-xl hover:shadow-red-500/30"
                >
                  Apply Now
                  <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </DarkSection>
          </Reveal>

          <p className="pb-4 text-center text-sm text-gray-500">
            Questions about this role?{' '}
            <a
              href={`mailto:contact@appsytec.com?subject=${encodeURIComponent(`Enquiry: ${rfq.ref_code || 'N/A'} - ${rfq.title || 'Position'}`)}`}
              className="font-semibold text-red-500 transition-colors hover:text-red-600 hover:underline"
            >
              Email us
            </a>{' '}
            and we'll be happy to help.
          </p>
        </div>
      </div>
    </PublicLayout>
  );
};

export default PublicJobListing;
